<template>
  <v-card flat tile class="participant-detail-nav nav-container" style="border-bottom: 1px solid #ededed">
    <div class="participant-detail-nav__bar">
      <div class="participant-detail-nav__left">
        <WorkspaceMenu />

        <button
          type="button"
          class="participant-detail-nav__back"
          aria-label="Back"
          @click="onBack"
        >
          <i class="ri-arrow-left-line"></i>
        </button>

        <nav class="participant-detail-nav__breadcrumb" aria-label="Breadcrumb">
          <router-link :to="listPath" class="participant-detail-nav__crumb">
            Participants
          </router-link>
          <span class="participant-detail-nav__separator">/</span>
          <v-skeleton-loader
            v-if="isParticipantNameLoading"
            type="text"
            class="participant-detail-nav__name-skeleton"
          />
          <span
            v-else
            class="participant-detail-nav__crumb participant-detail-nav__crumb--current"
          >
            {{ participantName }}
          </span>
        </nav>
      </div>

      <div class="participant-detail-nav__right">
        <template v-if="isProjectLevel">
          <span class="participant-detail-nav__date">
            <i class="ri-time-line"></i>
            <span>{{ formattedDate }}</span>
          </span>

          <button
            type="button"
            class="participant-detail-nav__analysis-btn"
            @click="onOpenAnalysis"
          >
            <span>Analysis</span>
            <i class="ri-external-link-line"></i>
          </button>
        </template>

        <template v-else>
          <HelpButton />
          <StartRecording />
        </template>
      </div>
    </div>
  </v-card>
</template>

<script>
import format from 'date-fns/format';
import { mapState } from 'vuex';
import WorkspaceMenu from '@/components/menus/WorkspaceMenu.vue';
import HelpButton from '@/components/buttons/HelpButton.vue';
import StartRecording from '@/components/misc/StartRecording.vue';

export default {
  name: 'ParticipantDetailNav',
  components: {
    WorkspaceMenu,
    HelpButton,
    StartRecording,
  },
  computed: {
    ...mapState(['participantDetailNav']),
    isProjectLevel() {
      return Boolean(this.$route.params.projectId);
    },
    projectId() {
      return this.$route.params.projectId || null;
    },
    listPath() {
      return this.isProjectLevel
        ? `/project/${this.projectId}/participants`
        : '/participants';
    },
    participantName() {
      const routeId = this.$route.params.participantId;
      if (
        routeId &&
        this.participantDetailNav?.id === routeId &&
        this.participantDetailNav?.name
      ) {
        return this.participantDetailNav.name;
      }
      return '';
    },
    isParticipantNameLoading() {
      return !this.participantName;
    },
    formattedDate() {
      return format(new Date(), 'EEE, d MMMM');
    },
  },
  methods: {
    onBack() {
      this.$router.push(this.listPath);
    },
    onOpenAnalysis() {
      if (!this.projectId) return;
      window.open(`/project/${this.projectId}/analysis?tab=tag`, '_blank');
    },
  },
};
</script>

<style scoped>
.participant-detail-nav {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  z-index: 100;
}

.participant-detail-nav__bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  min-height: 64px;
  padding: 0 16px;
  box-sizing: border-box;
  background: #ffffff;
}

.participant-detail-nav__left {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}

.participant-detail-nav__back {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  padding: 0;
  border: none;
  border-radius: 8px;
  background: transparent;
  color: #666666;
  cursor: pointer;
  flex-shrink: 0;
}

.participant-detail-nav__back:hover {
  background: #f5f5f5;
}

.participant-detail-nav__back i {
  font-size: 20px;
}

.participant-detail-nav__breadcrumb {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}

.participant-detail-nav__crumb {
  font-family: Inter, sans-serif;
  font-size: 16px;
  font-weight: 600;
  line-height: 150%;
  color: #999999;
  text-decoration: none;
  white-space: nowrap;
}

.participant-detail-nav__crumb--current {
  font-weight: 600;
  color: #4c4c4c;
  overflow: hidden;
  text-overflow: ellipsis;
}

.participant-detail-nav__name-skeleton {
  width: 140px;
  max-width: 40vw;
}

.participant-detail-nav__name-skeleton ::v-deep .v-skeleton-loader__text {
  margin: 0;
  height: 20px;
  border-radius: 4px;
}

.participant-detail-nav__separator {
  font-family: Inter, sans-serif;
  font-size: 16px;
  font-weight: 500;
  color: #999999;
}

.participant-detail-nav__right {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

.participant-detail-nav__date {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-right: 4px;
  font-family: Inter, sans-serif;
  font-size: 13px;
  font-weight: 500;
  line-height: 150%;
  color: #999999;
  white-space: nowrap;
}

.participant-detail-nav__date i {
  font-size: 16px;
}

.participant-detail-nav__analysis-btn {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  height: 37px;
  padding: 0 16px;
  border: none;
  border-radius: 32px;
  background: #5b7edb;
  color: #ffffff;
  cursor: pointer;
  white-space: nowrap;
}

.participant-detail-nav__analysis-btn:hover {
  background: #4a6bc7;
}

.participant-detail-nav__analysis-btn span {
  font-family: Inter, sans-serif;
  font-size: 14px;
  font-weight: 600;
  line-height: 150%;
  color: inherit;
  text-transform: capitalize;
}

.participant-detail-nav__analysis-btn i {
  font-size: 16px;
}
</style>
